import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, Tag, Button, Space, Typography, Upload, message, Modal, Descriptions } from 'antd';
import { 
  EyeOutlined, 
  FileTextOutlined, 
 
  PlusOutlined, 
  DownloadOutlined,
  DeleteOutlined,
  EditOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { getDocumentsByOwner } from '../../api/documents';
import { Document } from '../../types/document';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;

interface DocumentDetailTabProps {
  ownerId: string;
  ownerName: string;
  ownerType: 'resident' | 'employee';
  onBack: () => void;
}

const DocumentDetailTab: React.FC<DocumentDetailTabProps> = ({ 
  ownerId, 
  ownerName, 
  ownerType, 
  onBack 
}) => {
  const { t } = useTranslation();
  const { firestoreUser } = useAuth();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);

  // Fetch documents for the specific owner
  const { data: documentsData, isLoading: documentsLoading } = useQuery({
    queryKey: ['documentsByOwner', ownerId, ownerType, pagination.current, pagination.pageSize, firestoreUser?.workspace?.id],
    queryFn: () => getDocumentsByOwner({
      ownerId,
      ownerType,
      page: pagination.current,
      pageSize: pagination.pageSize,
      workspaceId: firestoreUser?.workspace?.id || '',
    }),
    enabled: !!firestoreUser?.workspace?.id && !!ownerId,
  });

  const handleUpload = (info: any) => {
    if (info.file.status === 'done') {
      message.success(`${info.file.name} ${t('common:upload')}`);
    } else if (info.file.status === 'error') {
      message.error(`${info.file.name} ${t('common:error')}`);
    }
  };

  const handleViewDocument = (document: Document) => {
    setSelectedDocument(document);
    setIsViewModalVisible(true);
  };

  const handleDownload = (document: Document) => {
    if (document.fileUrl) {
      const link = (window as any).document.createElement('a');
      link.href = document.fileUrl;
      link.download = document.name;
      (window as any).document.body.appendChild(link);
      link.click();
      (window as any).document.body.removeChild(link);
      message.success(t('documents:actions.download'));
    }
  };

  const handleDelete = (document: Document) => {
    Modal.confirm({
      title: t('documents:delete.confirmTitle'),
      content: t('documents:delete.confirmMessage', { name: document.name }),
      okText: t('common:actions.delete'),
      okType: 'danger',
      cancelText: t('common:actions.cancel'),
      onOk: () => {
        // TODO: Implement delete API call
        message.success(t('documents:delete.success'));
      },
    });
  };

  const columns = useMemo(() => [
    {
      title: t('documents:table.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Document) => (
        <Space direction="vertical" size="small">
          <Space>
            <FileTextOutlined style={{ color: '#1890ff' }} />
            <Text strong>{name}</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.description}
          </Text>
        </Space>
      ),
    },
    {
      title: t('documents:table.type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color="blue">{type}</Tag>
      ),
    },
    {
      title: t('documents:table.uploadDate'),
      dataIndex: 'uploadDate',
      key: 'uploadDate',
      render: (uploadDate: Date) => (
        <Text>{new Date(uploadDate).toLocaleDateString()}</Text>
      ),
    },
    {
      title: t('documents:table.expiryDate'),
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      render: (expiryDate: Date) => {
        if (!expiryDate) return '-';
        const date = new Date(expiryDate);
        const isExpired = date < new Date();
        return (
          <Text type={isExpired ? 'danger' : 'secondary'}>
            {date.toLocaleDateString()}
          </Text>
        );
      },
    },
    {
      title: t('documents:table.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'orange'}>
          {t(`documents:status.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      render: (_: any, record: Document) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewDocument(record)}
          >
            {t('documents:actions.view')}
          </Button>
          <Button
            icon={<DownloadOutlined />}
            size="small"
            onClick={() => handleDownload(record)}
          >
            {t('documents:actions.download')}
          </Button>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => {/* TODO: Implement edit */}}
          >
            {t('documents:actions.edit')}
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDelete(record)}
          >
            {t('documents:actions.delete')}
          </Button>
        </Space>
      ),
    },
  ], [t]);

  const handleTableChange = (pagination: any) => {
    setPagination(prev => ({
      ...prev,
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    }));
  };

  return (
    <Card>
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
              {t('documents:actions.back')}
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              {t('documents:documentsFor')} {ownerName}
            </Title>
          </Space>
          <Text type="secondary">
            {t('documents:detail.description', { 
              ownerType: ownerType === 'resident' ? 'resident' : 'employee', 
              ownerName 
            })}
          </Text>
        </Space>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Upload
            name="file"
            action="/api/upload"
            onChange={handleUpload}
            showUploadList={false}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            data={{ ownerId, ownerType }}
          >
            <Button icon={<PlusOutlined />} type="primary">
              {t('documents:actions.uploadDocument')}
            </Button>
          </Upload>
          <Button icon={<FileTextOutlined />}>
            {t('documents:actions.createFolder')}
          </Button>
        </Space>
      </div>
      
      <Table
        columns={columns}
        dataSource={documentsData?.data || []}
        loading={documentsLoading}
        rowKey="id"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: documentsData?.total || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            t('common:pagination.showing', { from: range[0], to: range[1], total }),
        }}
        onChange={handleTableChange}
      />

      {/* Document View Modal */}
      <Modal
        title={selectedDocument?.name}
        open={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        footer={[
          <Button key="download" icon={<DownloadOutlined />} onClick={() => selectedDocument && handleDownload(selectedDocument)}>
            {t('documents:actions.download')}
          </Button>,
          <Button key="close" onClick={() => setIsViewModalVisible(false)}>
            {t('common:actions.close')}
          </Button>
        ]}
        width={800}
      >
        {selectedDocument && (
          <div>
            <Descriptions column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label={t('documents:table.type')}>
                {selectedDocument.type}
              </Descriptions.Item>
              <Descriptions.Item label={t('documents:table.uploadDate')}>
                {new Date(selectedDocument.uploadDate).toLocaleDateString()}
              </Descriptions.Item>
              <Descriptions.Item label={t('documents:table.expiryDate')}>
                {selectedDocument.expiryDate ? new Date(selectedDocument.expiryDate).toLocaleDateString() : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('documents:table.status')}>
                <Tag color={selectedDocument.status === 'active' ? 'green' : 'orange'}>
                  {t(`documents:status.${selectedDocument.status}`)}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
            
            {selectedDocument.description && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>{t('common:description')}:</Text>
                <br />
                <Text>{selectedDocument.description}</Text>
              </div>
            )}

            {selectedDocument.fileUrl && (
              <div>
                <Text strong>{t('documents:documentPreview')}:</Text>
                <br />
                <iframe
                  src={selectedDocument.fileUrl}
                  width="100%"
                  height="400"
                  style={{ border: '1px solid #d9d9d9', borderRadius: '6px' }}
                  title={selectedDocument.name}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </Card>
  );
};

export default DocumentDetailTab;
